/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { useMutation, useQuery } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material'
import React, { useEffect, useState } from 'react'

import {
  GET_MASTER_CATEGORIES,
  GET_TENANT_DISPLAYS,
  GetMasterCategoriesQuery,
  GetTenantDisplaysQueryVariables,
  UPDATE_DISPLAY,
  UpdateDisplayMutation,
  UpdateDisplayMutationVariables,
} from '@/graphql'
import useT from '@/i18n/useT'

interface Props {
  open: boolean
  onClose: () => void
  storeId: string
  displayId: string
  initialName: string
  initialMasterCategoryId: string
  onSaved?: () => void
}

export const EditDisplayDialog: React.FC<Props> = ({
  open,
  onClose,
  storeId,
  displayId,
  initialName,
  initialMasterCategoryId,
  onSaved,
}) => {
  const { t } = useT()
  const [name, setName] = useState(initialName)
  const [masterCategoryId, setMasterCategoryId] = useState(
    initialMasterCategoryId,
  )
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setName(initialName)
      setMasterCategoryId(initialMasterCategoryId)
      setError(null)
    }
  }, [open, initialName, initialMasterCategoryId])

  const { data: masterCategoriesData, loading: masterCategoriesLoading } =
    useQuery<GetMasterCategoriesQuery>(GET_MASTER_CATEGORIES, {
      skip: !open,
    })

  const [updateDisplay, { loading: saving }] = useMutation<
    UpdateDisplayMutation,
    UpdateDisplayMutationVariables
  >(UPDATE_DISPLAY, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GET_TENANT_DISPLAYS,
        variables: {
          filters: { store: { id: storeId } },
          pagination: { limit: 100, offset: 0 },
        } as GetTenantDisplaysQueryVariables,
      },
    ],
  })

  const masterCategories = masterCategoriesData?.masterCategories.results || []

  const handleSave = async () => {
    setError(null)
    try {
      await updateDisplay({
        variables: {
          data: {
            id: displayId,
            name,
            masterCategoryId: masterCategoryId || undefined,
            store: { set: storeId },
          },
        },
      })
      onSaved?.()
      onClose()
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('display.updateError', 'Failed to update display'),
      )
    }
  }

  const unchanged =
    name === initialName && masterCategoryId === initialMasterCategoryId

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('display.edit', 'Edit Display')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t('display.name', 'Display Name')}
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
            size="small"
            autoFocus
          />
          <FormControl fullWidth size="small">
            <InputLabel>{t('display.masterCategory', 'Category')}</InputLabel>
            <Select
              value={masterCategoryId}
              label={t('display.masterCategory', 'Category')}
              onChange={e => setMasterCategoryId(e.target.value)}
              disabled={masterCategoriesLoading}
            >
              <MenuItem value="">
                <em>{t('display.noCategory', 'None')}</em>
              </MenuItem>
              {masterCategories.map(category => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!name.trim() || saving || unchanged}
        >
          {saving ? t('common.saving', 'Saving...') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
